// src/lib/metaTrack.js
//
// Fires a Meta conversion event through BOTH channels with one shared event_id:
//   1) browser Pixel — via a `meta_conversion` dataLayer push that the GTM tags
//      read (event name from meta_event_name, eventID from event_id).
//   2) server CAPI  — via POST /api/track.
// Meta deduplicates the two copies on (event_name + event_id).
//
// CONSENT TIMING (important): we must NOT push `meta_conversion` until Consent.js
// has applied consent to GTM via gtag('consent','update'). Otherwise the browser
// Pixel tag fires while GTM's Consent Mode is still 'denied' and gets blocked —
// losing the first event (e.g. ViewContent on the landing page). So every event
// is QUEUED and only flushed once:
//   - marketing consent is granted (getConsent().marketing), AND
//   - consent has been applied to GTM (window.__hambaConsentApplied, set by
//     Consent.js's applyToGoogle right before it dispatches 'hamba:consent-change').
// On decline, the queue simply never flushes. Tracking never throws into the UI.

import { getConsent } from '../homev2/Consent';
import { buildDashboardApiUrl } from '../dashboard/apiConfig';

// Consent.js dispatches this AFTER it calls gtag('consent','update').
const CONSENT_CHANGE_EVENT = 'hamba:consent-change';

function readCookie(name) {
  if (typeof document === 'undefined') return undefined;
  const m = document.cookie.match('(?:^|;)\\s*' + name + '\\s*=\\s*([^;]+)');
  return m ? decodeURIComponent(m[1]) : undefined;
}

function newEventId() {
  if (typeof window !== 'undefined' && window.crypto && window.crypto.randomUUID) {
    return window.crypto.randomUUID();
  }
  return 'e-' + Date.now() + '-' + Math.random().toString(16).slice(2);
}

// _fbc is set by the Pixel when a click arrives with ?fbclid=. If the cookie
// isn't there yet, reconstruct it from the URL. Format: fb.1.<timestamp>.<fbclid>
function resolveFbc() {
  const cookie = readCookie('_fbc');
  if (cookie) return cookie;
  try {
    const fbclid = new URLSearchParams(window.location.search).get('fbclid');
    if (fbclid) return `fb.1.${Date.now()}.${fbclid}`;
  } catch {
    /* ignore */
  }
  return undefined;
}

function marketingGranted() {
  const c = getConsent();
  return !!(c && c.marketing);
}

// Has Consent.js pushed the gtag consent update yet this page-load?
function consentApplied() {
  return typeof window !== 'undefined' && window.__hambaConsentApplied === true;
}

// Events captured before consent is ready wait here.
const queue = [];

// Build the payloads and fire both channels. `_fbp`/`_fbc` and the request body
// are read NOW (flush time) so the Pixel has had a chance to set `_fbp`.
function send(item) {
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'meta_conversion',
      meta_event_name: item.eventName,
      event_id: item.eventId,
      ...item.customData,
    });
  }

  const body = {
    event_name: item.eventName,
    event_id: item.eventId,
    event_source_url: item.eventSourceUrl,
    user_data: {
      em: item.userData.email || undefined,
      ph: item.userData.phone || undefined,
      fbp: readCookie('_fbp'),
      fbc: resolveFbc(),
    },
    custom_data: item.customData,
  };

  fetch(buildDashboardApiUrl('/api/track'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    keepalive: true,
  }).catch(() => { /* fire-and-forget */ });
}

function flush() {
  if (!consentApplied() || !marketingGranted()) return;
  while (queue.length) send(queue.shift());
}

// Flush whenever Consent.js (re)applies consent — this is the signal that fires
// AFTER gtag('consent','update'), so the browser Pixel tag won't be blocked.
if (typeof window !== 'undefined') {
  window.addEventListener(CONSENT_CHANGE_EVENT, flush);
}

/**
 * @param {'ViewContent'|'SubmitApplication'|'CompleteRegistration'} eventName
 * @param {{ userData?: { email?: string, phone?: string }, customData?: object }} [opts]
 */
export function trackMeta(eventName, opts = {}) {
  try {
    const { userData = {}, customData = {} } = opts;
    queue.push({
      eventName,
      eventId: newEventId(),            // shared by browser + server copies
      eventSourceUrl: typeof window !== 'undefined' ? window.location.href : undefined,
      userData,
      customData,
    });
    // Fire now if consent is already applied + granted; otherwise it waits for
    // the next 'hamba:consent-change' (i.e. when the user accepts).
    flush();
  } catch {
    /* never let tracking throw into the UI */
  }
}
